import React, { useState, useEffect, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameDay, isSameMonth } from 'date-fns';
import { es, enUS, de as deLocale } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { plannerRequestConfig } from '../lib/plannerRequest';
import { Loader2 } from 'lucide-react';

// Mismo tope que `.to_list(MAX_LIST_RESULTS)` del backend (server.py). Constante
// de cliente (no se lee del backend): una respuesta de rango de EXACTAMENTE 500
// es indistinguible de una truncada y dispara el fallback por-día (D5.2).
const MAX_LIST_RESULTS = 500;

export const MonthView = ({ selectedDate, onSelectDate, center }) => {
  const { api, language } = useAuth();
  const locale = language === 'es' ? es : language === 'de' ? deLocale : enUS;
  const [countsByDay, setCountsByDay] = useState({});
  const [loading, setLoading] = useState(true);

  const gridDays = useMemo(() => {
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: language === 'en' ? 0 : 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: language === 'en' ? 0 : 1 });
    const days = [];
    let d = gridStart;
    while (d <= gridEnd) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [selectedDate, language]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    // Colapsa las ~42 GET por-día (R4) en UNA request de rango sobre la rejilla
    // del mes. Límites Z construidos con la etiqueta de fecha LOCAL + sufijo
    // literal (misma semántica que `?date=` → `$regex ^<fecha>`), NO `toISOString()`
    // (desplazaría la fecha en husos != Z). El conteo por día se agrupa en cliente.
    const fetchMonth = async () => {
      setLoading(true);
      const [openH, openM] = (center?.openingTime || '08:00').split(':').map(Number);
      const [closeH, closeM] = (center?.closingTime || '21:00').split(':').map(Number);
      try {
        const startBound = `${format(gridDays[0], 'yyyy-MM-dd')}T00:00:00.000Z`;
        const endBound = `${format(gridDays[gridDays.length - 1], 'yyyy-MM-dd')}T23:59:59.999Z`;
        const res = await api.get(
          `/bookings?start_date=${encodeURIComponent(startBound)}&end_date=${encodeURIComponent(endBound)}`,
          plannerRequestConfig({ signal: controller.signal })
        );
        let data = res.data || [];
        // Fast path: <500 es demostrablemente completo (D5.2). ===500 es
        // potencialmente truncado por `.to_list(500)`: caemos al fan-out por-día
        // (42 GET `?date=`, presupuesto de 500/día) que preserva el conjunto
        // completo, bit a bit como el camino previo a 3.3. `=== 500` exacto, no
        // `>= 500` (`.to_list(500)` nunca devuelve más de 500). El conteo por-día
        // se agrupa después con la MISMA lógica en ambos caminos.
        if (data.length === MAX_LIST_RESULTS) {
          console.debug('[planner] range response saturated (500) → per-day fallback', { view: 'month' });
          const perDay = await Promise.all(
            gridDays.map(day =>
              api.get(`/bookings?date=${format(day, 'yyyy-MM-dd')}`, plannerRequestConfig({ signal: controller.signal }))
            )
          );
          data = perDay.flatMap(r => r.data || []);
        }
        if (!cancelled) {
          const map = {};
          for (const b of data) {
            if (b.status === 'CANCELLED') continue;
            const d = new Date(b.startDateTime);
            const h = d.getHours(), m = d.getMinutes();
            const inWindow = (h > openH || (h === openH && m >= openM))
                          && (h < closeH || (h === closeH && m < closeM));
            if (!inWindow) continue;
            // Agrupa por el prefijo de fecha del propio `startDateTime` (los 10
            // primeros chars), que es exactamente la etiqueta bajo la que la
            // fetch por-día (`?date=` → `$regex ^<fecha>`) contaba este booking.
            const dayKey = b.startDateTime.slice(0, 10);
            map[dayKey] = (map[dayKey] || 0) + 1;
          }
          setCountsByDay(map);
        }
      } catch {
        if (!cancelled) setCountsByDay({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMonth();
    return () => { cancelled = true; controller.abort(); };
  }, [api, gridDays, center]); // eslint-disable-line

  const weeks = useMemo(() => {
    const w = [];
    for (let i = 0; i < gridDays.length; i += 7) w.push(gridDays.slice(i, i + 7));
    return w;
  }, [gridDays]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col p-2 sm:p-4 overflow-auto">
      <div className="flex-1 min-h-0 flex flex-col max-w-2xl w-full mx-auto">
        <h2 className="text-xs sm:text-sm font-semibold text-zinc-300 uppercase tracking-wide mb-2 sm:mb-3 flex-shrink-0">
          {format(selectedDate, 'MMMM yyyy', { locale })}
        </h2>

        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 mb-1 flex-shrink-0">
          {(weeks[0] || []).map((day) => (
            <div key={day.toISOString()} className="text-center text-[9px] sm:text-[10px] text-zinc-500 uppercase tracking-wide py-1">
              {format(day, 'EEE', { locale })}
            </div>
          ))}
        </div>

        {/* Weeks */}
        <div className="flex-1 min-h-0 flex flex-col gap-px">
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 gap-px flex-1">
              {week.map((day) => {
                const dayKey = format(day, 'yyyy-MM-dd');
                const count = countsByDay[dayKey] || 0;
                const isToday = isSameDay(day, new Date());
                const isCurrentMonth = isSameMonth(day, selectedDate);
                const isSelected = isSameDay(day, selectedDate);

                return (
                  <button
                    key={dayKey}
                    onClick={() => onSelectDate(day)}
                    className={`
                      relative flex flex-col items-center justify-start pt-1.5 pb-1 min-h-[44px] rounded-sm transition-colors
                      ${isCurrentMonth ? 'text-zinc-200' : 'text-zinc-600'}
                      ${isSelected ? 'bg-indigo-900/60 ring-1 ring-indigo-500' : 'hover:bg-zinc-800'}
                      ${isToday && !isSelected ? 'ring-1 ring-zinc-600' : ''}
                    `}
                  >
                    <span className={`text-xs sm:text-sm font-medium ${isToday ? 'text-indigo-400' : ''}`}>
                      {format(day, 'd')}
                    </span>
                    {count > 0 && (
                      <span className={`mt-0.5 text-[9px] font-semibold px-1.5 rounded-full ${
                        isCurrentMonth ? 'bg-indigo-900/80 text-indigo-300' : 'bg-zinc-800 text-zinc-500'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
